import React from 'react'
import {useTranslation} from 'react-i18next'

import Box from 'src/designSystem/components/atoms/Box/Box'
import PFSvgContain from 'src/designSystem/components/atoms/PFSvg/PFSvgContain'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import Page from 'src/designSystem/components/organisms/Page/Page'
import {convertToDollarAmt} from 'src/lib/utils/numberUtil'
import {formatDate, friendlyDateWithDOW, utcDate} from 'src/lib/utils/date'
import PaymentSuccess from 'src/products/loans/MakePayment/LoanPaySuccess/PaymentSuccess.svg'

/**
 * This template is named MakePaymentSuccessTemplate rather than LoanPaySuccessTemplate because it is agnostic enough to be used for other products in the future.
 *
 * It is co-located with LoanPaySuccess right now because this it the only place it is used.
 *
 * This template is used to display a success message after a payment has been made.
 * It displays the payment amount, the date the payment will be executed, and a button to close the page.
 */

export type MakePaymentSuccessTemplateProps = {
  onOkay: () => void
  paymentAmount: string
  executeDate: string
}

const MakePaymentSuccessTemplate: React.FC<MakePaymentSuccessTemplateProps> = (
  props,
): JSX.Element => {
  const {onOkay, paymentAmount, executeDate} = props

  const {t} = useTranslation(['MakePaymentSuccess', 'Common'])

  return (
    <Page
      buttonProps={{
        type: 'singleButton',
        primary: {
          text: t('Common:OK'),
          onPress: () => onOkay(),
          testID: 'make-payment-success-cta',
        },
      }}
      variant={'generic'}
      smallTopGap
      testID={'make-payment-success-template'}
      banner={() => null}
    >
      <>
        <Box align={'center'} paddingBottom={'small'} paddingTop={'small'}>
          <PFSvgContain width={124} height={120} svg={PaymentSuccess} />
        </Box>
        <Box gap={'little'} align={'center'} paddingBottom={'large'}>
          <PFText variant={'h2'}>{t('YourPaymentOf')}</PFText>
          <PFText variant={'d2'} color={'info'}>
            {`${convertToDollarAmt(paymentAmount)}`}
          </PFText>
          <PFText variant={'h2'}>{t('isProcessing')}</PFText>
        </Box>
        <Box align={'center'} gap={'little'}>
          <PFText variant={'p'} color={'textDisabled'}>
            {t('YourPaymentWillBeInitiatedOn')}
          </PFText>
          <PFText variant={'p_semibold'}>
            {formatDate(executeDate, friendlyDateWithDOW, utcDate)}
          </PFText>
        </Box>
      </>
      <Box align={'center'} flex justify={'end'} paddingHorizontal={'small'}>
        <PFText variant={'p_sm'} color={'textDisabled'}>
          {t('PaymentsMadeTimingInformation')}
        </PFText>
      </Box>
    </Page>
  )
}

export {MakePaymentSuccessTemplate}
